<template>
  <div data-cy="api-keys">
    <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
      <p class="m-0 max-w-3xl text-sm text-gray-600">
        An API key lets you build your own integration.
      </p>
      <button
        v-if="!readOnly"
        type="button"
        class="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60"
        :disabled="loading || loadFailed || notAvailable"
        data-cy="api-key-create"
        @click="openCreate"
      >
        Create API key
      </button>
    </div>

    <div v-if="loading" class="flex justify-center py-10">
      <Spinner />
    </div>

    <Alert
      v-else-if="notAvailable"
      type="info"
      data-cy="api-access-not-available"
      text="API access isn't available on this account yet."
    />

    <Alert v-else-if="loadFailed" type="error" data-cy="api-keys-error">
      Your API keys couldn't be loaded.
      <button type="button" class="link ml-1" @click="load">Try again</button>
    </Alert>

    <template v-else>
      <Alert
        v-if="readOnly"
        type="warning"
        class="mb-4"
        data-cy="api-keys-read-only"
        :text="API_KEYS_READ_ONLY_COPY"
      />

      <Alert
        v-if="zapierKeyCount"
        type="info"
        class="mb-4"
        data-cy="api-keys-zapier-link"
      >
        You also have {{ zapierKeyCount }} Zapier API
        {{ zapierKeyCount === 1 ? "key" : "keys" }}. They're not listed here.
        <a href="/marketplace/zapier" class="link ml-1"
          >Manage them in Zapier</a
        >
      </Alert>

      <p
        v-if="!apiKeys.length"
        class="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600"
        data-cy="api-keys-empty"
      >
        No API keys yet.
      </p>

      <div v-else class="space-y-4" data-cy="api-keys-list">
        <section
          v-for="apiKey in apiKeys"
          :key="apiKey.id"
          class="overflow-hidden rounded-lg border border-gray-200 bg-white"
          :data-cy="`api-key:${apiKey.id}`"
        >
          <div class="border-b border-gray-200 bg-gray-50 px-4 py-2">
            <h3 class="m-0 text-sm font-semibold text-gray-900">
              {{ apiKey.name }}
            </h3>
            <p class="m-0 text-xs text-gray-500">
              Created {{ formatDateWithTimezone(apiKey.created_at) }}
              <template v-if="apiKey.created_by"
                >by {{ apiKey.created_by }}</template
              >
            </p>
          </div>
          <div
            class="hidden border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase text-gray-500 md:grid md:grid-cols-[1.3fr_2fr_0.8fr_1fr_1fr_auto] md:gap-4"
          >
            <span>Key</span>
            <span>Access</span>
            <span>Status</span>
            <span>Last used</span>
            <span>Expires</span>
            <span class="sr-only">Actions</span>
          </div>
          <div
            v-for="credential in apiKey.credentials"
            :key="credential.id"
            class="border-b border-gray-100 px-4 py-3 text-sm last:border-b-0"
            :data-cy="`api-key-credential:${credential.id}`"
          >
            <div
              class="grid grid-cols-1 gap-1 md:grid-cols-[1.3fr_2fr_0.8fr_1fr_1fr_auto] md:items-center md:gap-4"
            >
              <div class="min-w-0">
                <code class="text-xs text-gray-900"
                  >{{ credential.token_prefix }}…</code
                >
                <div class="text-xs text-gray-500">
                  Created {{ formatDateWithTimezone(credential.created_at) }}
                  <template v-if="credential.created_by"
                    >by {{ credential.created_by }}</template
                  >
                </div>
              </div>
              <div class="text-gray-700" data-cy="api-key-access">
                {{ accessLabel(credential.scopes) }}
              </div>
              <div>
                <span
                  class="whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold"
                  :class="apiKeyStatusStyle(credential.status).pill"
                  data-cy="api-key-status"
                  >{{ apiKeyStatusStyle(credential.status).label }}</span
                >
              </div>
              <div class="text-gray-700" data-cy="api-key-last-used">
                <span
                  v-if="credential.last_used_at"
                  :title="formatDateTimeWithTimezone(credential.last_used_at)"
                  >{{ fromNow(credential.last_used_at, true) }}</span
                >
                <span v-else class="text-gray-500">Never</span>
              </div>
              <div class="text-gray-700" data-cy="api-key-expires">
                <span
                  v-if="credential.revoked_at"
                  class="text-gray-500"
                  :title="formatDateTimeWithTimezone(credential.revoked_at)"
                  >Revoked
                  {{ formatDateWithTimezone(credential.revoked_at) }}</span
                >
                <span
                  v-else-if="credential.expires_at"
                  :title="formatDateTimeWithTimezone(credential.expires_at)"
                  >{{ formatDateWithTimezone(credential.expires_at) }}</span
                >
                <span v-else class="text-gray-500">Never</span>
              </div>
              <div
                v-if="credential.status === 'active'"
                class="flex flex-wrap gap-2 md:justify-end"
              >
                <button
                  v-if="!readOnly"
                  type="button"
                  :class="SECONDARY"
                  :data-cy="`api-key-roll:${credential.id}`"
                  @click="openRoll(apiKey, credential)"
                >
                  Roll
                </button>
                <button
                  type="button"
                  :class="SECONDARY"
                  :data-cy="`api-key-revoke:${credential.id}`"
                  @click="revoking = credential.id"
                >
                  Revoke
                </button>
              </div>
              <div v-else />
            </div>
            <InlineConfirm
              v-if="revoking === credential.id"
              class="mt-3"
              confirm-label="Revoke key"
              danger
              :busy="acting"
              @confirm="revoke(apiKey, credential)"
              @cancel="revoking = null"
            >
              Revoke <code>{{ credential.token_prefix }}…</code>? Anything using
              it stops working right away. This can't be undone.
            </InlineConfirm>
          </div>
        </section>
      </div>
    </template>

    <ApiKeyForm
      v-if="formOpen && meta"
      :scopes="meta.scopes"
      :expiry-days="meta.expiry_days"
      :rolling="rolling"
      @close="closeForm"
      @minted="replaceApiKey"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import {
  formatDateTimeWithTimezone,
  formatDateWithTimezone
} from "@/lib/helpers";

import Alert from "@/components/Seranova/Alert/Alert.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import { fromNow } from "@/mixins";
import type {
  ApiKey,
  ApiKeyCredential,
  ApiKeysResponse
} from "@/vuex/modules/apiKeys";
import InlineConfirm from "../Webhooks/InlineConfirm.vue";
import {
  API_KEYS_READ_ONLY_COPY,
  apiKeyError,
  apiKeyStatusStyle
} from "./apiAccess";
import ApiKeyForm from "./ApiKeyForm.vue";

const SECONDARY =
  "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-60";

// Emitted once the first load has finished, whatever its outcome.
const emit = defineEmits<{ settled: [] }>();

const store = useStore();

const apiKeys = ref<ApiKey[]>([]);
const meta = ref<ApiKeysResponse["meta"] | null>(null);
const loading = ref(false);
const loadFailed = ref(false);
// Every route answers 404 while API access is off for the account.
const notAvailable = ref(false);
const formOpen = ref(false);
const rolling = ref<{
  apiKey: ApiKey;
  credential: ApiKeyCredential;
} | null>(null);
const revoking = ref<string | null>(null);
const acting = ref(false);

const readOnly = computed(() => !!meta.value?.read_only);
const zapierKeyCount = computed(() => meta.value?.zapier_keys?.count ?? 0);

async function load() {
  loading.value = true;
  loadFailed.value = false;
  try {
    const response: ApiKeysResponse = await store.dispatch("fetchApiKeys");
    apiKeys.value = response.data;
    meta.value = response.meta;
  } catch (error) {
    if (apiKeyError(error, "").status === 404) notAvailable.value = true;
    else loadFailed.value = true;
  } finally {
    loading.value = false;
  }
}

// Every scope ticked reads as general access, as it was chosen.
function accessLabel(scopes: string[]): string {
  const offered = meta.value?.scopes ?? [];
  if (offered.length && offered.every((scope) => scopes.includes(scope.key))) {
    return "General access";
  }
  return scopes
    .map((key) => offered.find((scope) => scope.key === key)?.name ?? key)
    .join(", ");
}

function openCreate() {
  if (readOnly.value) return;
  rolling.value = null;
  formOpen.value = true;
}

function openRoll(apiKey: ApiKey, credential: ApiKeyCredential) {
  if (readOnly.value) return;
  revoking.value = null;
  rolling.value = { apiKey, credential };
  formOpen.value = true;
}

function closeForm() {
  formOpen.value = false;
  rolling.value = null;
}

function replaceApiKey(apiKey: ApiKey) {
  const index = apiKeys.value.findIndex((k) => k.id === apiKey.id);
  if (index === -1) apiKeys.value.push(apiKey);
  else apiKeys.value.splice(index, 1, apiKey);
}

async function revoke(apiKey: ApiKey, credential: ApiKeyCredential) {
  acting.value = true;
  try {
    const updated: ApiKey = await store.dispatch("revokeApiKey", {
      apiKeyId: apiKey.id,
      credentialId: credential.id
    });
    replaceApiKey(updated);
    revoking.value = null;
    window.notify.success("Key revoked.");
  } catch (error) {
    window.notify.error(
      apiKeyError(error, "The key couldn't be revoked. Try again.").message
    );
  } finally {
    acting.value = false;
  }
}

onMounted(async () => {
  await load();
  emit("settled");
});
</script>
